import { CloudUploadOutlined } from "@ant-design/icons";
import { Button, Modal, Select } from "antd";
import React, { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa"; // Added FaPlus and FaTrash for add/remove icons
import { getDateColumns } from "./modules/getDateColumns";
import { useBulkData } from "./modules/useBulkData";
import { useChannelMappings } from "./modules/useChannelMappings";
import { useFetchData } from "./modules/useFetchData";
import { useRatePlans } from "./modules/useRatePlans";
import { useRoomManagement } from "./modules/useRoomManagement";
import { useSyncToChannels } from "./modules/useSyncToChannels";
import { useTimeSelection } from "./modules/useTimeSelection";

import { AvailabilityManagement } from "./molecules/availabilityManagement";
import { BulkUpdateRatesAndAvailabilityModal } from "./molecules/bulkUpdateRatesAndAvailabilityModal";
import { CloseoutManagement } from "./molecules/closeoutManagement";
import { DateColumnsManagement } from "./molecules/dateColumnsManagement";
import { ManualUpdateRoomModal } from "./molecules/manualUpdateRoomModal";
import { RackRateManagement } from "./molecules/rackRateManagement";
import { RatePlansManagement } from "./molecules/ratePlansManagement";

function RoomManagementTemplate() {
  const [modalVisible, setModalVisible] = useState(false);
  const [availableRooms, setAvailableRooms] = useState("");
  const [showRestrictions, setShowRestrictions] = useState(false);
  // Closeout modal state
  // visibility derived from closeoutModalRoomId
  const [closeoutModalRoomId, setCloseoutModalRoomId] = useState(null);
  const [closeoutModalDate, setCloseoutModalDate] = useState(null);
  const [closeoutModalAll, setCloseoutModalAll] = useState(false);
  const [closeoutModalSelectedPlans, setCloseoutModalSelectedPlans] = useState(
    []
  );

  // Bulk update state
  const [bulkModalVisible, setBulkModalVisible] = useState(false);

  const {
    rooms,
    ratePlans,
    closeout,
    availability,
    rentPerDayByDate,
    minimumStay,
    maximumStay,
    setCloseout,
    setAvailability,
    setRentPerDayByDate,
    setMinimumStay,
    setMaximumStay,
    setRatePlans,
  } = useFetchData();

  const {
    selectedType,
    selectedRatePlanId,
    selectedRoomId,
    selectedDate,
    manualRate,
    handleManualRateChange,
    handleCellClick,
    handleModalOk,
  } = useRatePlans({
    rooms,
    ratePlans,
    availability,
    rentPerDayByDate,
    availableRooms,
    minimumStay,
    maximumStay,
    closeout,
    setAvailableRooms,
    setModalVisible,
    setCloseout,
    setAvailability,
    setRentPerDayByDate,
    setMinimumStay,
    setMaximumStay,
    setRatePlans,
  });

  const {
    handleAvailabilityChange,
    // handleCloseoutToggle provided by hook but overridden here to open modal
    handleMinimumStayChange,
    handleMaximumStayChange,
  } = useRoomManagement({
    selectedRatePlanId,
    selectedRoomId,
    selectedDate,
    closeout,
    minimumStay,
    maximumStay,
    setCloseout,
    setAvailableRooms,
    setMinimumStay,
    setMaximumStay,
  });

  // handlers for the closeout modal (moved to component but persistence delegated to useBulkData)
  const handleConfirmCloseoutModal = async () => {
    const roomId = closeoutModalRoomId;
    const dateKey = closeoutModalDate;
    const closeoutSelectedRatePlans = !!closeoutModalAll
      ? (ratePlans[roomId] || []).map((rp) => rp._id)
      : closeoutModalSelectedPlans;
    const ok = await applyCloseout(
      roomId,
      dateKey,
      closeoutModalAll,
      closeoutSelectedRatePlans
    );
    if (ok) setCloseoutModalRoomId(null);
  };

  const handleCancelCloseoutModal = () => {
    setCloseoutModalRoomId(null);
  };

  const {
    currentMonth,
    selectedYear,
    handleMonthYearChange,
    nextMonth,
    prevMonth,
  } = useTimeSelection();

  const { syncingRoomId, handleSyncToChannels } = useSyncToChannels({
    currentMonth,
    selectedYear,
  });

  const { mappingsByRoomAndPlan, fetchMappings } = useChannelMappings();

  const {
    bulkInfo,
    setBulkInfo,
    handleAddDateRange,
    handleRemoveDateRange,
    handleDateRangeChange,
    handleBulkModalOk,
    handleBulkModalCancel,
    applyCloseout,
  } = useBulkData({
    closeout,
    availability,
    minimumStay,
    maximumStay,
    rentPerDayByDate,
    ratePlans,
    setCloseout,
    setAvailability,
    setRentPerDayByDate,
    setMinimumStay,
    setMaximumStay,
    setRatePlans,
    setBulkModalVisible,
    rooms,
  });

  // Open the closeout modal for a given room/date and initialize its state
  const openCloseoutModal = (roomId, dateKey) => {
    setCloseoutModalRoomId(roomId);
    setCloseoutModalDate(dateKey);

    const existing =
      (closeout && closeout[roomId] && closeout[roomId][dateKey]) || null;
    if (existing && typeof existing === "object") {
      setCloseoutModalAll(!!existing.all);
      setCloseoutModalSelectedPlans(
        Array.isArray(existing.ratePlans) ? existing.ratePlans : []
      );
    } else {
      setCloseoutModalAll(false);
      setCloseoutModalSelectedPlans([]);
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
  };

  const dateColumns = getDateColumns(currentMonth, selectedYear);

  const openBulkModal = () => {
    setBulkModalVisible(true);
  };

  return (
    <div className="container main-calendar">
      <h1>Manage Rate & Availability</h1>

      <div className="calendar-container mb-3">
        <Button
          className="button-select"
          onClick={openBulkModal}
          style={{ marginLeft: "10px" }}
        >
          Bulk Update
        </Button>
        <Button
          className="button-select"
          onClick={() => setShowRestrictions(!showRestrictions)}
          style={{ marginLeft: "10px" }}
        >
          {showRestrictions ? "Hide Restrictions" : "Restrictions"}
        </Button>
        <Button className="button-select" onClick={prevMonth}>
          <FaChevronLeft />
        </Button>
        <Button className="button-select" onClick={nextMonth}>
          <FaChevronRight />
        </Button>
        <DatePicker
          selected={currentMonth.toDate()}
          onChange={handleMonthYearChange}
          dateFormat="MMM YYYY"
          showMonthYearPicker
          className="month-year-picker"
        />
      </div>

      <div className="table-container">
        <table className="room-table">
          <DateColumnsManagement dateColumns={dateColumns} />
          <tbody>
            {rooms.map((room, index) => (
              <React.Fragment key={room._id}>
                {index > 0 && (
                  <tr className="room-divider">
                    <td
                      colSpan={dateColumns.length + 2}
                      style={{ height: "15px" }}
                    ></td>
                  </tr>
                )}

                <tr>
                  <tr
                    className="sticky-room-name"
                    rowSpan={4 + (ratePlans[room._id]?.length || 0)}
                  >
                    <strong>{room.name}</strong>
                    <Button
                      size="small"
                      icon={<CloudUploadOutlined />}
                      loading={syncingRoomId === room._id}
                      onClick={() => handleSyncToChannels(room._id)}
                      style={{ marginLeft: 8 }}
                      title="Push this month's availability/rates/restrictions to connected OTA channels"
                    >
                      Sync to Channels
                    </Button>
                  </tr>
                </tr>

                <AvailabilityManagement
                  dateColumns={dateColumns}
                  currentMonth={currentMonth}
                  selectedYear={selectedYear}
                  availability={availability}
                  closeout={closeout}
                  handleCellClick={handleCellClick}
                  roomId={room._id}
                  maxcount={room.maxcount}
                />

                {showRestrictions && (
                  <CloseoutManagement
                    dateColumns={dateColumns}
                    currentMonth={currentMonth}
                    selectedYear={selectedYear}
                    closeout={closeout}
                    openCloseoutModal={openCloseoutModal}
                    roomId={room._id}
                  />
                )}
                <RackRateManagement
                  dateColumns={dateColumns}
                  currentMonth={currentMonth}
                  selectedYear={selectedYear}
                  rentPerDayByDate={rentPerDayByDate}
                  handleCellClick={handleCellClick}
                  roomId={room._id}
                  closeout={closeout}
                  rentperday={room.rentperday}
                />
                <RatePlansManagement
                  ratePlans={ratePlans}
                  dateColumns={dateColumns}
                  currentMonth={currentMonth}
                  selectedYear={selectedYear}
                  rentPerDayByDate={rentPerDayByDate}
                  handleCellClick={handleCellClick}
                  roomId={room._id}
                  closeout={closeout}
                  rentperday={room.rentperday}
                  showRestrictions={showRestrictions}
                  minimumStay={minimumStay}
                  maximumStay={maximumStay}
                  mappingsByRoomAndPlan={mappingsByRoomAndPlan}
                  onRefetchMappings={fetchMappings}
                />
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {modalVisible && (
        <ManualUpdateRoomModal
          selectedType={selectedType}
          rooms={rooms}
          manualRate={manualRate}
          selectedDate={selectedDate}
          modalVisible={modalVisible}
          selectedRatePlanId={selectedRatePlanId}
          availableRooms={availableRooms}
          ratePlans={ratePlans}
          selectedRoomId={selectedRoomId}
          minimumStay={minimumStay}
          maximumStay={maximumStay}
          handleModalOk={handleModalOk}
          handleModalCancel={handleModalCancel}
          handleAvailabilityChange={handleAvailabilityChange}
          handleManualRateChange={handleManualRateChange}
          handleMinimumStayChange={handleMinimumStayChange}
          handleMaximumStayChange={handleMaximumStayChange}
        />
      )}

      {/** Closeout modal for selecting rate plans */}
      <Modal
        title={`Closeout for ${closeoutModalDate || ""}`}
        visible={!!closeoutModalRoomId}
        onOk={handleConfirmCloseoutModal}
        onCancel={handleCancelCloseoutModal}
      >
        <div style={{ marginBottom: 12 }}>
          <label>
            <input
              type="checkbox"
              checked={!!closeoutModalAll}
              onChange={(e) => setCloseoutModalAll(e.target.checked)}
            />
            &nbsp;Close all rate plans for this date
          </label>
        </div>
        {!closeoutModalAll && (
          <div>
            <label>Select rate plans to close for this room/date</label>
            <Select
              mode="multiple"
              style={{ width: "100%" }}
              value={closeoutModalSelectedPlans}
              onChange={(vals) => setCloseoutModalSelectedPlans(vals)}
              optionFilterProp="children"
              allowClear
              placeholder="Select rate plans"
            >
              {(ratePlans[closeoutModalRoomId] || []).map((rp) => (
                <Select.Option key={rp._id} value={rp._id}>
                  {rp.rateplanname}
                </Select.Option>
              ))}
            </Select>
          </div>
        )}
      </Modal>

      {bulkModalVisible && (
        <BulkUpdateRatesAndAvailabilityModal
          bulkInfo={bulkInfo}
          bulkModalVisible={bulkModalVisible}
          rooms={rooms}
          ratePlans={ratePlans}
          handleBulkModalOk={handleBulkModalOk}
          handleBulkModalCancel={handleBulkModalCancel}
          handleAddDateRange={handleAddDateRange}
          handleDateRangeChange={handleDateRangeChange}
          handleRemoveDateRange={handleRemoveDateRange}
          setBulkInfo={setBulkInfo}
        />
      )}
    </div>
  );
}

export default RoomManagementTemplate;
